import { Component, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { genTinyGUID, UlConfig } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { DocumentOwnerData } from '@workflow/ecp-shared/model/documentMetaData.model';
import { RequestedDocMetaData } from '@workflow/ecp-shared/model/requestedDocMetaData.model';
import { FileDetail } from '@workflow/ecp-shared/model/filedetail.model';
import { DocumentType } from '@workflow/ecp-shared/enum/enum';
import { Document } from '@workflow/ecp-shared/model/document.model';
import { AlertStatus, Info } from '@workflow/ecp-shared/constant/information.const';
import { RequestedDocument } from '@workflow/ecp-shared/model/requested.document.model';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { AppConfig } from '@app/config/config.type';
const loginType = `api:loginType`;

/** This component dynamically generates requested documents. */
@BuildingBlock('generate-requested-doc')
@Component({
  selector: 'ecp-dynamic-requested-doc',
  templateUrl: './requested-doc.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./requested-doc.component.scss'],
  standalone: false
})
export class RequestedDocumentComponent extends BuildingBlockControlBaseComponent implements OnInit {
  protected logger = inject(SawDust);

  /** On Init */
  @Input() public applicant: DocumentOwnerData;
  /** Trigger upload document close event */
  @Output() public documentClos;
  /** Trigger upload document close event */
  @Output() public documentClose = new EventEmitter<{ [key: string]: string }>();
  /** subscriber for modal popup */
  private readonly timeOut = 1000;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public retryFiles: any[];
  /** showFileUpload */
  public showFileUpload = false;
  /** Value to hold the Alert status icon. */
  public alertStatus = 'error';
  /** Value to hold alert message. */
  public alertMessage = '';
  /** Hold the value to Show and hide alert. */
  public showAlert = false;
  /** Return file submit status */
  public submitSuccess = false;
  /** Configuration for Shared-UI ea-document-upload component
  /* For scanBaseURL, we need to set this as "undefined" until the configuration loads
  /* This way, we can prevent initialization until undefined's value is changed */
  public docUploadConfig: UlConfig = {
    acceptedFiles: '.png, .jpg, .jpeg, .pdf',
    maxFiles: 100,
    maxFilesize: 10,
    uploadMultiple: true
  };
  /** Flg indicates file upload inprogress  */
  public inprogress = false;

  /** Will hold meta data for document */
  public documentMetadata: RequestedDocMetaData;
  /** requestedDocs */
  public requestedDocs: FileDetail[] = [];
  /** app configuration */
  private appConfig: AppConfig;
  public get isRequired(): boolean {
    return this.field.required || this.field?.config?.required || !!this.logicService.hasFunction('required', this.field.config.validation);
  }
  public uniqueId = genTinyGUID();
  /** Initialization */
  public ngOnInit(): void {
    this.appConfig = this.runtimeConfigService.getConfig;
    // If this is null or an empty string, the expected behavior is no scan
    this.docUploadConfig.scanBaseURL = this.dynamicScanBaseURL;
    this.docUploadConfig.malwareApiToken = this.appConfig?.malwareScan?.malwareApiToken;

    this.documentMetadata = {} as RequestedDocMetaData;
    this.getRequestedDocuments();
  }

  /** Need to dynamically derive the baseURL to implement opt out functionality */
  get dynamicScanBaseURL(): string {
    return this.appConfig?.malwareScan?.apiBaseURL ?? '';
  }
  /** uploadDoc */
  public uploadDoc(): void {
    this.showFileUpload = true;
    this.submitSuccess = false;
  }

  public documentPageHeader(): string {
    return 'Document Upload';
  }

  public onDocumentClose() {
    this.showFileUpload = false;
  }

  public closeAlert(): void {
    this.showAlert = false;
  }

  /**
   * Event trigger to close the upload document section
   */
  public close(): void {
    this.showFileUpload = false;
  }

  /**
   * Get document type based on application flow logic
   * TODO: Update logic to get other document types when leveraging for other applications
   */
  public getDocumentType(): string | null {
    const product = this.statefulService.wrap(loginType).valueStringOnly.toLowerCase();
    return product === 'oao' ? DocumentType.MemberDocuments.toString() : null;
  }

  private getSanitizedFilename(): string {
    return this.commonService.sanitizeFilename(this.getFileName());
  }

  /**
   * Get file name
   */
  public getFileName(): string | null {
    const maxLength = 95;
    const currentDate = new Date();
    const formattedDateTime = this.customDateTime(currentDate);
    const applicantName = `${this.applicant?.firstName} ${this.applicant?.lastName}`;

    // Without Filename Prefix
    let fileName = `${applicantName}-${formattedDateTime}`;
    // Handling the rare scenario when the length of ApplicantName-Timestamp is greater than the maxLength:95
    if (fileName.length > maxLength) {
      const trimmedName = applicantName.substring(0, applicantName.length - (fileName.length - maxLength));
      fileName = `${trimmedName}-${formattedDateTime}`;

      return fileName.replace(/ /g, '_');
    }

    // With Filename Prefix
    const fileNamePrefix = this.field.config?.filenamePrefix?.length > 0 ? this.field.config?.filenamePrefix : 'arcDX';
    if (fileNamePrefix.length > 0) {
      let fileNameWithPrefix = `${fileNamePrefix}-${applicantName}-${formattedDateTime}`;
      // Handling the scenario when the length of Prefix-ApplicantName-Timestamp is greater than the maxLength:95
      if (fileNameWithPrefix.length > maxLength) {
        const trimmedPrefix = this.field.config?.filenamePrefix
          ?.toString()
          .substring(0, this.field.config?.filenamePrefix?.length - (fileNameWithPrefix.length - maxLength));
        fileNameWithPrefix = `${trimmedPrefix}-${applicantName}-${formattedDateTime}`;

        return fileNameWithPrefix.replace(/ /g, '_');
      } else {
        return fileNameWithPrefix.replace(/ /g, '_');
      }
    } else {
      return fileName.replace(/ /g, '_');
    }
  }

  /**
   * Get custom date time
   */
  customDateTime(date: Date): string {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString();

    return `${year}${month}${day}_${hours}${minutes}${seconds}_${milliseconds}`;
  }

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Submited for Review
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onSaveclick(files: any[]): void {
    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    this.logEvent('ecp.ui.uploadDocumentIntra', { eventState: 'start', correlationId: eventPairId });
    this.inprogress = true;
    if (files && files.length > 0) {
      files = files.filter((t) => !t.isDeleted);
      files.forEach((file) => {
        file.status = Info.queued;
        file.upload = {
          filename: file.name
        };
      });
    }
    this.documentMetadata.referenceId = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    this.documentMetadata.documentKey = this.field.config.storageId.split('/').pop();
    this.documentMetadata.isRequired = this.field.required;
    this.applicationApiService.RequestedDocUpload(
      files,
      this.documentMetadata,
      this.getDocumentType(),
      this.getSanitizedFilename(),
      this.field?.config?.documentCategory,
      (result: Document[], errorList: []) => {
        this.saveDocsToBundle(result);
        this.submitSuccess = !errorList || errorList.length === 0 ? true : false;

        if (this.submitSuccess) {
          setTimeout(() => {
            this.documentClose.emit({ data: Info.documentSuccess });
            this.onDocumentClose();
          }, this.timeOut);
        } else {
          this.ShowAlertMsg(Info.unableToUpload, AlertStatus.error);
          this.showErrorModal(errorList);
        }

        this.inprogress = false;
        this.logIntoApplicationInsights(eventPairId, this.submitSuccess ? 'success' : 'error', files);
      }
    );
  }

  /**
   * Logs an event into Application Insights with details about a file upload operation.
   * This method captures and logs the metadata of the uploaded files along with the status of the operation.
   *
   * @param eventPairId - A unique identifier for correlating this event with other related events.
   * @param status - Indicates the status of the file upload operation, either 'success' or 'error'.
   * @param files - An array of `File` objects that were involved in the upload operation.
   */
  private logIntoApplicationInsights(eventPairId: string, status: 'success' | 'error', files: File[]): void {
    const filesMetaData = this.processFileMetadataAI(files);
    this.logEvent('ecp.ui.uploadDocumentIntra', {
      eventState: 'end',
      correlationId: eventPairId,
      status,
      filesInfo: filesMetaData
    });
  }

  /**
   * Processes metadata of an array of files, calculating the total file count and total file size in kilobytes.
   *
   * @param An array of `File` objects to be processed.
   * @returns An object containing the total count of files and their combined size in kilobytes.
   * The object has two properties: `totalFileCount` and `totalFileSizeKb`.
   *  - `totalFileCount`: The total number of files processed.
   *  - `totalFileSizeKb`: The combined size of all files in kilobytes.
   */
  public processFileMetadataAI(files: File[]): { totalFileCount: number; totalFileSizeKb: number } {
    let totalFileCount = 0;
    let totalFileSizeKb = 0;

    files.forEach((file) => {
      totalFileCount++;
      if (file.size) totalFileSizeKb += file.size / 1024;
    });

    totalFileSizeKb = Math.ceil(totalFileSizeKb);
    return { totalFileCount, totalFileSizeKb };
  }

  /** SaveDocsToBundle */
  private saveDocsToBundle = (documents: Document[]): void => {
    const fieldValue = this.statefulService.wrap(this.field.config.storageId).valueString;
    let requestedDocument = {} as RequestedDocument;
    if (fieldValue && fieldValue.trim().length > 0) {
      requestedDocument = JSON.parse(fieldValue);
    }

    requestedDocument.name = this.field.config.label;
    requestedDocument.isRequired = this.field.required;

    if (!requestedDocument.documents) {
      requestedDocument.documents = [];
    }
    if (!documents || documents.length === 0) {
      this.field.data.value = JSON.stringify(requestedDocument);
      return;
    }

    documents.forEach((doc) => {
      const fileDetail = {
        description: doc.description,
        fileID: doc.documentId,
        fileName: doc.name,
        fileType: doc.contentMimeType,
        isScanned: true,
        uploadedBy: this.applicant.uploadedBy
      } as FileDetail;
      requestedDocument.documents.push(fileDetail);
    });

    this.field.data.value = JSON.stringify(requestedDocument);
    this.getRequestedDocuments();
  };

  /** Show model popup while document upload gets error  */
  public showErrorModal(files): void {
    this.retryFiles = files;
  }

  /** ShowAlertMsg */
  public ShowAlertMsg(alertMessage: string, alertStatus: string): void {
    this.alertMessage = alertMessage;
    this.alertStatus = alertStatus;
    this.showAlert = true;
  }

  public getRequestedDocuments(): void {
    const docs = this.field.data.value as string;
    this.requestedDocs = [];
    if (!docs) {
      return;
    }
    const docs_obj = JSON.parse(docs);

    if (!docs_obj || !docs_obj.documents) {
      return;
    }

    this.requestedDocs = docs_obj.documents;
    this.requestedDocs = this.requestedDocs.filter((t) => t.uploadedBy === this.applicant.uploadedBy);
  }

  public get isAddMoreFiles(): boolean {
    return this.requestedDocs.length > 0 ? true : false;
  }
}
